import { Injectable } from '@angular/core';
import { HomeDashboardService } from 'app/services/dashboard/home-dashboard.service';
import {
    HomeDashboard,
    HomeFilters,
    HomeSection,
} from 'app/types/home-dashboard.type';
import {
    EMPTY,
    Observable,
    filter,
    finalize,
    map,
    of,
    shareReplay,
} from 'rxjs';
import { sortedStations } from '../utils/station-selection';

/** Cuándo lo guardado se muestra pero se vuelve a pedir en segundo plano. */
const STALE_AFTER_MS = 60_000;

const ALL_SECTIONS: HomeSection[] = [
    'fulfillment',
    'optimization',
    'unproduced',
    'gate',
    'waste',
];

/** De qué respuesta salió el contenido de una sección y cuándo se pidió. */
type Stamp = { version: number; generatedAt: string; at: number };

export type StoredHome = {
    data: HomeDashboard;
    requestedAt: number;
    sections: Partial<Record<HomeSection, Stamp>>;
};

const homeKey = (filters: HomeFilters): string =>
    [
        filters.range.from,
        filters.range.to,
        filters.subsidiaryIds.length ? sortedStations(filters.subsidiaryIds).join('-') : 'all',
        filters.cycleId ?? 'all',
        ...(filters.gateSubsidiaryId != null
            ? [`gate-${filters.gateSubsidiaryId}`]
            : []),
    ].join('|');

const sameRun = (
    a: { version: number; generatedAt: string },
    b: { version: number; generatedAt: string }
): boolean => a.version === b.version && a.generatedAt === b.generatedAt;

@Injectable({ providedIn: 'root' })
export class HomeDashboardStore {
    private readonly _homes = new Map<string, StoredHome>();
    private readonly _inFlight = new Map<string, Observable<HomeDashboard>>();

    constructor(private _service: HomeDashboardService) {}

    peek(filters: HomeFilters): StoredHome | undefined {
        return this._homes.get(homeKey(filters));
    }

    /** Lo guardado de la combinación hace más de 60 s; sin nada guardado, no. */
    isStale(filters: HomeFilters): boolean {
        const stored = this._homes.get(homeKey(filters));

        return !!stored && Date.now() - stored.requestedAt >= STALE_AFTER_MS;
    }

    /** El inicio de la combinación: lo guardado sin pedir, o el pedido con la sección elegida. */
    home(filters: HomeFilters, section: HomeSection): Observable<HomeDashboard> {
        const key = homeKey(filters);
        const stored = this._homes.get(key);
        if (stored) {
            return of(stored.data);
        }

        const requestKey = `${key}#first:${section}`;

        return (
            this._inFlight.get(requestKey) ??
            this.track(
                [requestKey],
                this._service.getHome(filters, section),
                (data, current) => {
                    if (current) {
                        this._homes.set(key, {
                            data,
                            requestedAt: Date.now(),
                            sections: { [section]: this.stamp(data) },
                        });
                    }

                    return data;
                }
            )
        );
    }

    /**
     * Vuelve a pedir el inicio con la sección elegida. Si la corrida y la hora de los
     * datos no cambiaron devuelve el mismo objeto que ya estaba guardado.
     */
    refresh(
        filters: HomeFilters,
        section: HomeSection
    ): Observable<HomeDashboard> {
        const key = homeKey(filters);
        const requestKey = `${key}#home:${section}`;

        return (
            this._inFlight.get(requestKey) ??
            this.track(
                [requestKey],
                this._service.getHome(filters, section),
                (fresh, current) => {
                    const stored = this._homes.get(key);
                    if (!current || !stored) {
                        return fresh;
                    }

                    stored.requestedAt = Date.now();
                    stored.sections[section] = this.stamp(fresh);
                    const unchanged =
                        sameRun(stored.data, fresh) &&
                        !!stored.data.sections[section];
                    if (!unchanged) {
                        stored.data = {
                            ...fresh,
                            sections: {
                                ...stored.data.sections,
                                ...fresh.sections,
                            },
                        };
                    }

                    return stored.data;
                }
            )
        );
    }

    /** Las secciones que todavía no tienen contenido, en un solo pedido. */
    sections(filters: HomeFilters): Observable<HomeDashboard> {
        const stored = this._homes.get(homeKey(filters));
        if (!stored) {
            return EMPTY;
        }

        const missing = ALL_SECTIONS.filter(
            (section) => !stored.data.sections[section]
        );
        if (!missing.length) {
            return of(stored.data);
        }

        const key = homeKey(filters);
        const notRequested = missing.filter(
            (section) => !this._inFlight.has(`${key}#section:${section}`)
        );

        return notRequested.length
            ? this.fetchSections(filters, notRequested)
            : this._inFlight.get(`${key}#section:${missing[0]}`);
    }

    /**
     * Una sección: reutiliza el pedido en vuelo que ya la incluye, no pide si se guardó
     * hace menos de 60 s y, si no, la pide de nuevo.
     */
    section(
        filters: HomeFilters,
        section: HomeSection
    ): Observable<HomeDashboard> {
        const key = homeKey(filters);
        const stored = this._homes.get(key);
        if (!stored) {
            return EMPTY;
        }

        const inFlight = this._inFlight.get(`${key}#section:${section}`);
        if (inFlight) {
            return inFlight;
        }

        const stamp = stored.sections[section];
        const fresh =
            !!stored.data.sections[section] &&
            !!stamp &&
            Date.now() - stamp.at < STALE_AFTER_MS;

        return fresh ? of(stored.data) : this.fetchSections(filters, [section]);
    }

    clear(): void {
        this._homes.clear();
        this._inFlight.clear();
    }

    private fetchSections(
        filters: HomeFilters,
        wanted: HomeSection[]
    ): Observable<HomeDashboard> {
        const key = homeKey(filters);

        return this.track(
            wanted.map((section) => `${key}#section:${section}`),
            this._service.getSections(filters, wanted),
            (response, current) => {
                const stored = this._homes.get(key);
                if (!current || !stored) {
                    return null;
                }

                const incoming = wanted.filter(
                    (section) => response.sections[section]
                );
                const unchanged = incoming.every(
                    (section) =>
                        !!stored.data.sections[section] &&
                        !!stored.sections[section] &&
                        sameRun(stored.sections[section], response)
                );
                incoming.forEach((section) => {
                    stored.sections[section] = this.stamp(response);
                });
                if (!unchanged) {
                    stored.data = {
                        ...stored.data,
                        sections: { ...stored.data.sections, ...response.sections },
                    };
                }

                return stored.data;
            }
        );
    }

    private stamp(run: { version: number; generatedAt: string }): Stamp {
        return {
            version: run.version,
            generatedAt: run.generatedAt,
            at: Date.now(),
        };
    }

    /**
     * Comparte un pedido en vuelo bajo cada una de sus claves, lo cancela cuando se van
     * todos los que esperan y entrega `apply` solo si el pedido sigue vigente (si no hubo
     * un `clear` en el medio). Un resultado nulo no se entrega.
     */
    private track<R>(
        keys: string[],
        source: Observable<R>,
        apply: (value: R, current: boolean) => HomeDashboard | null
    ): Observable<HomeDashboard> {
        const request: Observable<HomeDashboard> = source.pipe(
            map((value) => apply(value, this._inFlight.get(keys[0]) === request)),
            filter((data): data is HomeDashboard => data !== null),
            finalize(() =>
                keys.forEach((key) => {
                    if (this._inFlight.get(key) === request) {
                        this._inFlight.delete(key);
                    }
                })
            ),
            shareReplay({ bufferSize: 1, refCount: true })
        );
        keys.forEach((key) => this._inFlight.set(key, request));

        return request;
    }
}
